import type { EffectChannel } from "../effects";
import type { Cascade } from "../types";
import { BOARD_PIXELS, BOARD_SIZE, cascadeEffectStrength, TILE_SIZE } from "./constants";
export function createCombatFlights({
  currentEffectScale,
  reducedMotion,
  isDisposed,
  host,
  flightLayer,
  trackDOMAnimation,
}: {
  currentEffectScale: (channel?: EffectChannel) => number;
  reducedMotion: boolean;
  isDisposed: () => boolean;
  host: HTMLDivElement;
  flightLayer: HTMLDivElement;
  trackDOMAnimation: (animation: Animation) => Animation;
}) {
  const emitCombatFlights = (cascade: Cascade) => {
    const effectScale = currentEffectScale("particles");
    if (reducedMotion || isDisposed() || effectScale <= 0 || cascade.groups.length === 0) return;
    const strength = cascadeEffectStrength(cascade);
    const targetNames = ["enemy", "mana", "block", "key", "wood", "ore"];
    const rect = host.getBoundingClientRect();
    const scaleX = rect.width / BOARD_PIXELS;
    const scaleY = rect.height / BOARD_PIXELS;
    let emitted = 0;
    cascade.groups.forEach((group, groupIndex) => {
      if (flightLayer.childElementCount > 172) return;
      const targetName = targetNames[group.tile];
      const target = targetName
        ? document.querySelector<HTMLElement>(`[data-effect-target="${targetName}"]`)
        : null;
      if (!target || group.cells.length === 0) return;
      const center = group.cells.reduce(
        (point, cell) => ({
          x: point.x + (cell % BOARD_SIZE) * TILE_SIZE + TILE_SIZE / 2,
          y: point.y + Math.floor(cell / BOARD_SIZE) * TILE_SIZE + TILE_SIZE / 2,
        }),
        { x: 0, y: 0 },
      );
      const startX = rect.left + (center.x / group.cells.length) * scaleX;
      const startY = rect.top + (center.y / group.cells.length) * scaleY;
      const targetRect = target.getBoundingClientRect();
      const endX = targetRect.left + targetRect.width / 2;
      const endY = targetRect.top + targetRect.height / 2;
      const dx = endX - startX;
      const dy = endY - startY;
      const distance = Math.hypot(dx, dy);
      const direction = groupIndex % 2 === 0 ? 1 : -1;
      const perpendicularX = distance > 0 ? -dy / distance : 0;
      const perpendicularY = distance > 0 ? dx / distance : 0;
      const curve = Math.min(86, 22 + distance * 0.12) * direction * (0.7 + strength.boost * 0.3);
      const controlX = dx * 0.5 + perpendicularX * curve;
      const controlY = dy * 0.5 + perpendicularY * curve;
      const delay = groupIndex * Math.max(20, 38 - strength.boost * 12);
      const duration = 180 + Math.min(100, distance * 0.12);
      const mote = document.createElement("b");
      const impact = document.createElement("i");
      mote.className = `match-flight-mote tile-${group.tile}`;
      impact.className = `match-impact-burst tile-${group.tile}${group.tile === 0 ? " is-strike" : ""}`;
      mote.style.setProperty("--fx-strength", String(effectScale));
      impact.style.setProperty("--fx-strength", String(effectScale));
      mote.style.left = `${startX}px`;
      mote.style.top = `${startY}px`;
      impact.style.left = `${endX}px`;
      impact.style.top = `${endY}px`;
      const pointTransform = (progress: number, scale: number) => {
        const inverse = 1 - progress;
        const x = 2 * inverse * progress * controlX + progress * progress * dx;
        const y = 2 * inverse * progress * controlY + progress * progress * dy;
        return `translate(calc(-50% + ${Math.round(x)}px), calc(-50% + ${Math.round(y)}px)) rotate(${(Math.atan2(dy, dx) * 180) / Math.PI}deg) scale(${scale})`;
      };
      const trails: HTMLElement[] = [];
      const trailAnimations: Animation[] = [];
      const trailCount = 2 + Math.round(effectScale * 2 + strength.boost * 2);
      for (let trailIndex = 0; trailIndex < trailCount; trailIndex++) {
        const trail = document.createElement("i");
        const lag = (trailIndex + 1) / (trailCount + 2);
        const start = Math.max(0, 0.08 - lag * 0.08);
        const midpoint = Math.max(0.1, 0.5 - lag * 0.16);
        const end = Math.max(0.16, 0.92 - lag * 0.14);
        trail.className = `match-flight-trail tile-${group.tile}`;
        trail.style.setProperty("--fx-strength", String(effectScale));
        trail.style.left = `${startX}px`;
        trail.style.top = `${startY}px`;
        trails.push(trail);
        flightLayer.append(trail);
        trailAnimations.push(
          trackDOMAnimation(
            trail.animate(
              [
                { transform: pointTransform(start, 0.35), opacity: 0 },
                {
                  transform: pointTransform(midpoint, 0.45 + strength.boost * 0.22),
                  opacity: (0.25 + effectScale * 0.38) * (1 - trailIndex / (trailCount + 2)),
                },
                { transform: pointTransform(end, 0.25), opacity: 0 },
              ],
              { duration, delay, easing: "cubic-bezier(.18,.72,.24,1)", fill: "forwards" },
            ),
          ),
        );
      }
      flightLayer.append(mote, impact);
      const moteAnimation = trackDOMAnimation(
        mote.animate(
          [
            { transform: pointTransform(0, 0.55), opacity: 0 },
            {
              transform: pointTransform(0.5, 0.9 + strength.boost * 0.22),
              opacity: 0.45 + effectScale * 0.55,
              offset: 0.48,
            },
            {
              transform: pointTransform(1, 1.08 + strength.boost * 0.12),
              opacity: 0,
              offset: 0.94,
            },
          ],
          { duration, delay, easing: "cubic-bezier(.2,.8,.2,1)", fill: "forwards" },
        ),
      );
      const impactAnimation = trackDOMAnimation(
        impact.animate(
          [
            { transform: "translate(-50%, -50%) scale(.25) rotate(-18deg)", opacity: 0 },
            {
              transform: "translate(-50%, -50%) scale(.72) rotate(0deg)",
              opacity: 0.45 + effectScale * 0.55,
              offset: 0.3,
            },
            {
              transform: `translate(-50%, -50%) scale(${1.08 + strength.boost * 0.2}) rotate(12deg)`,
              opacity: 0,
            },
          ],
          {
            duration: 180 + effectScale * 100 + strength.boost * 50,
            delay: delay + duration * 0.72,
            easing: "cubic-bezier(.12,.72,.18,1)",
            fill: "forwards",
          },
        ),
      );
      const fragmentCount = Math.min(
        180 - flightLayer.childElementCount,
        Math.round((8 + strength.boost * 10) * effectScale),
      );
      for (let piece = 0; piece < fragmentCount; piece++) {
        const fragment = document.createElement("i");
        fragment.className = `match-flight-trail match-impact-fragment tile-${group.tile}`;
        fragment.style.left = `${endX}px`;
        fragment.style.top = `${endY}px`;
        const angle = piece * 2.39996 + groupIndex;
        const reach = 22 + (piece % 5) * 9;
        const vx = Math.cos(angle) * reach;
        const vy = Math.sin(angle) * reach - 18;
        fragment.style.width = `${piece % 3 === 0 ? 7 : 3}px`;
        fragment.style.height = `${piece % 3 === 0 ? 3 : 4}px`;
        trails.push(fragment);
        flightLayer.append(fragment);
        trailAnimations.push(
          trackDOMAnimation(
            fragment.animate(
              [
                { transform: "translate(-50%, -50%)", opacity: 0, offset: 0 },
                {
                  transform: `translate(${Math.round(vx * 0.25)}px, ${Math.round(vy * 0.25)}px)`,
                  opacity: effectScale,
                  offset: 0.12,
                },
                {
                  transform: `translate(${Math.round(vx * 0.7)}px, ${Math.round(vy * 0.7 + 9)}px) rotate(90deg)`,
                  opacity: effectScale * 0.8,
                  offset: 0.55,
                },
                {
                  transform: `translate(${Math.round(vx)}px, ${Math.round(vy + 32)}px) rotate(180deg)`,
                  opacity: 0,
                },
              ],
              {
                duration: 320 + (piece % 3) * 45,
                delay: delay + duration * 0.78,
                easing: "linear",
                fill: "both",
              },
            ),
          ),
        );
      }
      void Promise.allSettled([
        ...trailAnimations.map((animation) => animation.finished),
        moteAnimation.finished,
        impactAnimation.finished,
      ]).then(() => {
        trails.forEach((trail) => trail.remove());
        mote.remove();
        impact.remove();
      });
      emitted++;
    });
    if (emitted > 0)
      host.dataset.combatFlightCount = String(
        Number(host.dataset.combatFlightCount ?? "0") + emitted,
      );
  };
  const playBoardRecoil = (cascade: Cascade) => {
    const effectScale = currentEffectScale("impact");
    const swordCount = cascade.groups
      .filter((group) => group.tile === 0)
      .reduce((total, group) => total + group.count, 0);
    if (reducedMotion || effectScale <= 0 || swordCount === 0) return;
    const strength = cascadeEffectStrength(cascade);
    const amplitude = Math.min(
      3.5,
      (0.72 + swordCount * 0.1 + strength.boost * 1.05) * effectScale,
    );
    const direction = cascade.layer % 2 === 0 ? -1 : 1;
    trackDOMAnimation(
      host.animate(
        [
          { transform: "translate(0, 0)" },
          {
            transform: `translate(${direction * amplitude}px, ${-amplitude * 0.35}px)`,
            offset: 0.28,
          },
          {
            transform: `translate(${-direction * amplitude * 0.55}px, ${amplitude * 0.2}px)`,
            offset: 0.58,
          },
          { transform: "translate(0, 0)" },
        ],
        { duration: 105 + effectScale * 55, easing: "ease-out" },
      ),
    );
    host.dataset.recoilCount = String(Number(host.dataset.recoilCount ?? "0") + 1);
  };
  return { emitCombatFlights, playBoardRecoil };
}
